/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * Maps frontend action names to backend authorization API actions
 *
 * This module provides the mapping between:
 * - Frontend permission action strings (e.g., 'draft-delete-playlist')
 * - Backend authorization API actions (e.g., 'DELETE_PLAYLIST')
 * - Request types (tutorialRequests, playlistRequests, etc.)
 * - Domains (lcp, tbw)
 */

export type ResourceType = 'tutorial' | 'playlist' | 'collection' | 'query' | 'upload' | 'tag' | 'user' | 'userRecord';

// Domain constants
export const DOMAIN_LCP = 'lcp' as const;
export const DOMAIN_TBW = 'tbw' as const;

export type Domain = typeof DOMAIN_LCP | typeof DOMAIN_TBW;

export interface ActionMapping {
    /** Backend authorization action name */
    backendAction: string;

    /** Resource type for batch authorization request */
    resourceType: ResourceType;

    /** Domain (lcp or tbw) */
    domain: Domain;

    /** Whether this action requires specific resource data */
    requiresResource: boolean;

    /** Description for documentation */
    description?: string;
}

/**
 * Content Production (LCP) Action Mappings
 */
export const CP_ACTION_MAP: Record<string, ActionMapping> = {
    // Tutorial Actions - Draft
    'draft-publish-utut-to-live': {
        backendAction: 'PUBLISH_TUTORIAL_TO_LIVE',
        resourceType: 'tutorial',
        domain: 'lcp',
        requiresResource: true,
        description: 'Publish a draft tutorial to live',
    },
    'draft-send-utut-to-localization': {
        backendAction: 'SEND_TUTORIAL_TO_LOC',
        resourceType: 'tutorial',
        domain: 'lcp',
        requiresResource: true,
        description: 'Send draft tutorial to localization',
    },
    'draft-refresh-utut-for-tutorial-builder': {
        backendAction: 'REFRESH_TUTORIAL_FROM_LOC',
        resourceType: 'tutorial',
        domain: 'lcp',
        requiresResource: true,
        description: 'Refresh draft tutorial from Tutorial Builder',
    },

    // Tutorial Actions - Live
    'live-archive-utut': {
        backendAction: 'ARCHIVE_TUTORIAL',
        resourceType: 'tutorial',
        domain: 'lcp',
        requiresResource: true,
        description: 'Archive a live tutorial',
    },
    'live-unarchive-utut': {
        backendAction: 'UNARCHIVE_TUTORIAL',
        resourceType: 'tutorial',
        domain: 'lcp',
        requiresResource: true,
        description: 'Unarchive an archived tutorial',
    },

    // Playlist Actions - Draft
    'draft-create-new-playlist': {
        backendAction: 'UPDATE_PLAYLIST',
        resourceType: 'playlist',
        domain: 'lcp',
        requiresResource: true,
        description: 'Check if user can create playlists (uses placeholder resource)',
    },

    // Playlist Actions - Live
    'live-create-new-playlist': {
        backendAction: 'UPDATE_PLAYLIST',
        resourceType: 'playlist',
        domain: 'lcp',
        requiresResource: true,
        description: 'Check if user can create live playlists (uses placeholder resource)',
    },
    'edit-playlist': {
        backendAction: 'UPDATE_PLAYLIST',
        resourceType: 'playlist',
        domain: 'lcp',
        requiresResource: true,
        description: 'Edit a playlist (works for both draft and live - status determined by resource)',
    },
    'delete-playlist': {
        backendAction: 'DELETE_PLAYLIST',
        resourceType: 'playlist',
        domain: 'lcp',
        requiresResource: true,
        description: 'Delete a playlist (works for both draft and live - status determined by resource)',
    },
    // Collection Actions - Draft
    'draft-create-new-collection': {
        backendAction: 'UPDATE_COLLECTION',
        resourceType: 'collection',
        domain: 'lcp',
        requiresResource: true,
        description: 'Check if user can create collections (uses placeholder resource)',
    },
    // Collection Actions - Live
    'live-create-new-collection': {
        backendAction: 'UPDATE_COLLECTION',
        resourceType: 'collection',
        domain: 'lcp',
        requiresResource: true,
        description: 'Check if user can create live collections (uses placeholder resource)',
    },
    'edit-collection': {
        backendAction: 'UPDATE_COLLECTION',
        resourceType: 'collection',
        domain: 'lcp',
        requiresResource: true,
        description: 'Edit a collection (works for both draft and live - status determined by resource)',
    },
    'delete-collection': {
        backendAction: 'DELETE_COLLECTION',
        resourceType: 'collection',
        domain: 'lcp',
        requiresResource: true,
        description: 'Delete a collection (works for both draft and live - status determined by resource)',
    },
    // View/Capability Actions
    'view-filters-utut-baby-locale': {
        backendAction: 'SEARCH_TUTORIALS',
        resourceType: 'query',
        domain: 'lcp',
        requiresResource: false,
        description: 'Check if user can view tutorial filters',
    },
    'search-tutorials': {
        backendAction: 'SEARCH_TUTORIALS',
        resourceType: 'query',
        domain: 'lcp',
        requiresResource: false,
        description: 'Search tutorials',
    },
    'search-playlists': {
        backendAction: 'SEARCH_PLAYLISTS',
        resourceType: 'query',
        domain: 'lcp',
        requiresResource: false,
        description: 'Search playlists',
    },
    'search-collections': {
        backendAction: 'SEARCH_COLLECTIONS',
        resourceType: 'query',
        domain: 'lcp',
        requiresResource: false,
        description: 'Search collections',
    },
    'view-user-management': {
        backendAction: 'LIST_USER_RECORDS',
        resourceType: 'userRecord',
        domain: 'lcp',
        requiresResource: false,
        description: 'Check if user can view User Management',
    },
};

/**
 * Tutorial Builder Web (TBW) Action Mappings
 */
export const TBW_ACTION_MAP: Record<string, ActionMapping> = {
    // View/List Actions
    'view-tutorial-builder-web': {
        backendAction: 'GET_TAGS',
        resourceType: 'tag',
        domain: 'tbw',
        requiresResource: false,
        description: 'Check if user can view Tutorial Builder Web (available to all TBW roles)',
    },
    'list-tutorials-as-author': {
        backendAction: 'LIST_TUTORIALS_AS_AUTHOR',
        resourceType: 'query',
        domain: 'tbw',
        requiresResource: false,
        description: 'List tutorials as author',
    },
    'list-tutorials-as-curator': {
        backendAction: 'LIST_TUTORIALS_AS_CURATOR',
        resourceType: 'query',
        domain: 'tbw',
        requiresResource: false,
        description: 'List tutorials as curator',
    },
    'list-tutorials-as-qe': {
        backendAction: 'LIST_TUTORIALS_AS_QE',
        resourceType: 'query',
        domain: 'tbw',
        requiresResource: false,
        description: 'List tutorials as QE',
    },
    'list-tutorials-as-locqe': {
        backendAction: 'LIST_TUTORIALS_AS_LOCQE',
        resourceType: 'query',
        domain: 'tbw',
        requiresResource: false,
        description: 'List tutorials as LocQE',
    },
    'list-tutorials-as-production': {
        backendAction: 'LIST_TUTORIALS_AS_PRODUCTION',
        resourceType: 'query',
        domain: 'tbw',
        requiresResource: false,
        description: 'List tutorials as production',
    },
    'list-tutorials-as-readonly': {
        backendAction: 'LIST_TUTORIALS_AS_READONLY',
        resourceType: 'query',
        domain: 'tbw',
        requiresResource: false,
        description: 'List tutorials as readonly (all published)',
    },

    // Create/Edit/Delete Actions
    'manage-own-tutorial': {
        backendAction: 'MANAGE_OWN_TUTORIAL',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Create/Edit/Delete own tutorial (dev status only)',
    },
    'read-tutorial': {
        backendAction: 'READ_TUTORIAL',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Read tutorial details',
    },

    // Submit Actions
    'submit-as-author': {
        backendAction: 'SUBMIT_AS_AUTHOR',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Submit tutorial as author',
    },
    'submit-as-curator': {
        backendAction: 'SUBMIT_AS_CURATOR',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Submit tutorial as curator',
    },

    // Curator Actions
    'assign-to-curator': {
        backendAction: 'ASSIGN_TO_CURATOR',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Assign tutorial to curator',
    },
    'move-to-review': {
        backendAction: 'MOVE_TO_REVIEW',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Move tutorial to review',
    },
    'approve-as-curator': {
        backendAction: 'APPROVE_AS_CURATOR',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Approve tutorial as curator',
    },
    'reject-as-curator': {
        backendAction: 'REJECT_AS_CURATOR',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Reject tutorial as curator',
    },
    'create-new-version': {
        backendAction: 'CREATE_NEW_VERSION',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Create new version of published tutorial',
    },

    // QE Actions
    'approve-as-qe': {
        backendAction: 'APPROVE_AS_QE',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Approve tutorial as QE',
    },
    'reject-as-qe': {
        backendAction: 'REJECT_AS_QE',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Reject tutorial as QE',
    },

    // LocQE Actions
    'approve-as-locqe': {
        backendAction: 'APPROVE_AS_LOCQE',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Approve tutorial as LocQE',
    },
    'refresh-localization': {
        backendAction: 'REFRESH_LOCALIZATION',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Refresh localization for tutorial',
    },

    // Production Actions
    'reassign-author-creator': {
        backendAction: 'REASSIGN_AUTHOR_CREATOR',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Reassign tutorial author/creator',
    },

    // Other Actions
    'get-tags': {
        backendAction: 'GET_TAGS',
        resourceType: 'tag',
        domain: 'tbw',
        requiresResource: false,
        description: 'Get available tags',
    },
    'validate-potential-owner': {
        backendAction: 'VALIDATE_POTENTIAL_OWNER',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Validate potential tutorial owner',
    },
    'validate-urls': {
        backendAction: 'VALIDATE_URLS',
        resourceType: 'tutorial',
        domain: 'tbw',
        requiresResource: true,
        description: 'Validate tutorial URLs',
    },
    'view-user-management': {
        backendAction: 'LIST_USER_RECORDS',
        resourceType: 'userRecord',
        domain: 'tbw',
        requiresResource: false,
        description: 'Check if user can view User Management',
    },
};

/**
 * Get action mapping for a frontend action
 * @param frontendAction The frontend action string (e.g., 'draft-delete-playlist')
 * @param domain Optional domain to filter by. If provided, only checks that domain's map.
 * @returns ActionMapping object or null if not found
 */
export function getActionMapping(frontendAction: string, domain?: Domain): ActionMapping | null {
    if (domain) {
        // If domain is specified, only check that domain's map
        const actionMap = domain === DOMAIN_LCP ? CP_ACTION_MAP : TBW_ACTION_MAP;
        return actionMap[frontendAction] || null;
    }
    // If no domain specified, check both (LCP first for backward compatibility)
    return CP_ACTION_MAP[frontendAction] || TBW_ACTION_MAP[frontendAction] || null;
}

/**
 * Get resource type for a backend action by searching through all action mappings
 * @param backendAction The backend action string (e.g., 'SEARCH_TUTORIALS')
 * @returns ResourceType or null if not found
 */
export function getResourceTypeForBackendAction(backendAction: string): ResourceType | null {
    // Search through all action maps to find matching backend action
    const allMaps = [CP_ACTION_MAP, TBW_ACTION_MAP];

    for (const actionMap of allMaps) {
        for (const mapping of Object.values(actionMap)) {
            if (mapping.backendAction === backendAction) {
                return mapping.resourceType;
            }
        }
    }

    return null;
}

/**
 * Resource type to request body key mapping
 * Maps ResourceType to the key used in batch authorization request body
 */
export const RESOURCE_TYPE_TO_REQUEST_KEY: Record<ResourceType, string> = {
    tutorial: 'tutorialRequests',
    playlist: 'playlistRequests',
    collection: 'collectionRequests',
    query: 'queryRequests',
    upload: 'uploadRequests',
    tag: 'tagRequests',
    user: 'userRequests',
    userRecord: 'userRecordRequests',
} as const;

/**
 * Resource type to response key mapping
 * Maps ResourceType to the key used in batch authorization response
 */
export const RESOURCE_TYPE_TO_RESPONSE_KEY: Record<ResourceType, string> = {
    tutorial: 'tutorialResults',
    playlist: 'playlistResults',
    collection: 'collectionResults',
    query: 'queryResults',
    upload: 'uploadResults',
    tag: 'tagResults',
    user: 'userResults',
    userRecord: 'userRecordResults',
} as const;
